import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';

import {
  BASENAME, CHARGE_KIND, CHARGE_KIND_DISPLAY, LAYOUT_TYPES, RELATIONSHIP_OPTIONS, UNSELECTED_LABEL, ENABLE_GOTO_AGREEMENT_FEATURE
} from '../../../constants';
import Loading from '../../atoms/Loading';
import LineTitleGray from '../../atoms/titles/LineTitleGray';
import LineTitle from '../../atoms/titles/LineTitle';
import RadioRoom from '../../atoms/form/RadioRoom';
import SelectBox from '../../atoms/form/SelectBox';
import LinkButton from '../../atoms/button/LinkButton';
import InfantCaption from '../../atoms/InfantCaption';
import TooltipIcon from '../../molecules/TooltipIcon';
import { setCurrentStep, setErrorMessages, setLayoutType, setOnCloseErrorModal } from '../../../modules/app';
import CustomerForm from '../../molecules/CustomerForm';
import { createEmptyArray } from '../../../utils';
import { scrollToTop } from '../../../utils/scroll';
import { makeSessionAgreeGuest, makeSessionMemberAndAccompany } from '../../../utils/input';
import { updateSession, useOtherSiteId } from '../../../modules/session';
import { addUnselectedOption } from '../../../utils/search';
import { applyErrorStyle, getValidationErrorMessages, removeErrorStyle } from '../../../utils/error';
import { FDataFromSession } from '../../Head';
import environments from '../../../environments';
import { getIsAna2 } from '../../../utils/Ana2';
import { getRomanAlphabetString } from '../../../utils/romanAlphabet';

const Entry = ({ session }) => {
  const currentStep = 3;
  const dispatch = useDispatch();
  const [fetching, setFetching] = useState(false);
  const agreementFormRef = useRef();

  const {
    otherSiteIdData, otherSiteMemberId, setOtherSiteMemberId,
    showOtherSiteId, editOtherSiteId, fetching: fetchingOtherSiteId,
    requiredOtherSiteId, inputAttributeDisplay,
  } = useOtherSiteId();

  useEffect(() => {
    dispatch(
      setLayoutType(LAYOUT_TYPES.step)
    );
    dispatch(setCurrentStep(currentStep));

    return () => {
      dispatch(
        setLayoutType(LAYOUT_TYPES.default)
      );
    };
  }, []);

  const { state, setters } = useCustomerInfo(session);

  const {
    customers,
    nameEmergency,
    relationshipEmergency,
    phoneNumberEmergency1,
    phoneNumberEmergency2,
    phoneNumberEmergency3,
    message,
  } = state;

  const {
    setNameEmergency,
    setRelationshipEmergency,
    setPhoneNumberEmergency1,
    setPhoneNumberEmergency2,
    setPhoneNumberEmergency3,
    setMessage,
  } = setters;

  const representativeCustomer = customers.length ? customers.find((a) => a.state.representative) : undefined;

  const { member, plans, flightSchedule } = session;
  const email = member && member.email;
  const userId = member && member.userID;
  const { numberOfRooms, hotelID, prefectureID, stayNights, femaleOnlyPlan: femaleOnly } = plans[0];

  const departureDate = moment(flightSchedule.departure.departureTime).format('YYYY-MM-DD');
  const arrivalDate = moment(flightSchedule.arrival.arrivalTime).format('YYYY-MM-DD');

  const handleConfirm = async () => {
    // if (!validateState(state)) {
    //   dispatch(addErrorMessage('未入力の項目があります。'));
    //   dispatch(setOnCloseErrorModal(scrollToTop));
    //   return;
    // }

    setFetching(true);

    removeErrorStyle('pageEntry');

    const otherSiteMemberIdValue = editOtherSiteId ? otherSiteMemberId : undefined;
    const sessionAccompany = makeSessionMemberAndAccompany(state, email, arrivalDate, otherSiteMemberIdValue);

    const onFailed = (errors) => {
      applyErrorStyle(errors);

      const messages = getValidationErrorMessages(errors);
      dispatch(setErrorMessages(messages));
      dispatch(setOnCloseErrorModal(scrollToTop));

      setFetching(false);
    };

    const onSuccess = () => {
      const isNotGuest = !!(session.member && session.member.userID);
      if (ENABLE_GOTO_AGREEMENT_FEATURE && isNotGuest) {
        agreementFormRef.current.submit();
      } else {
        dispatch(updateSession(
          makeSessionAgreeGuest(),
          () => window.open(`${BASENAME}/final_confirm`, '_self'),
          onFailed,
        ));
      }
      setFetching(false);
    };

    dispatch(updateSession(sessionAccompany, onSuccess, onFailed));
  };

  const optionalMessage = '※任意　ご記入いただけないと、適切に対応できない場合があります';

  const relationShipOptions = addUnselectedOption(RELATIONSHIP_OPTIONS, UNSELECTED_LABEL);

  const agreeCallbackUrl = `${environments.API_BASE_URI}/agreeResult`;

  const SelectedOutboundFlight = flightSchedule.departure;
  const SelectedOutboundDate = SelectedOutboundFlight.departureTime.substr(0, 10);
  const isAna2 = getIsAna2(SelectedOutboundFlight.airline, SelectedOutboundDate);

  const infantNum = session.people.infant || 0;
  const childrenCNum = session.people.childrenC || 0;
  const children2Num = session.people.children2 || 0;
  const peopleNumWithoutInfant = customers.length - infantNum - childrenCNum - children2Num;
  const indicatorOptions = customers.filter((c) => c.state.type === CHARGE_KIND.adult).map((c, i) => {
    const indicatorValue = String(i + 1);
    const name = i === 0 ? '代表者' : `同行者${i}`;
    return {
      id: indicatorValue,
      name,
      value: indicatorValue
    };
  });
  const haveAnyInfant = (infantNum + childrenCNum + children2Num) > 0;

  return (
    <div id="pageEntry" className="pageEntry">
      <FDataFromSession session={session} step={currentStep} />
      <Loading show={fetching || fetchingOtherSiteId} progressRate={100} hideProgressBar />
      <div className="EntryBlock">
        <LineTitleGray size="lg">お客様情報入力</LineTitleGray>
        <div className="EntryBody">
          <div className="EntryBodyItem">
            <LineTitle text="代表者情報" className="flexTitle"><span className="required">※必須</span></LineTitle>
            {representativeCustomer && (
              <CustomerForm id="representative" representative customer={representativeCustomer} index={0} femaleOnly={femaleOnly} isAna2={isAna2}/>
            )}
            <div className="EntryFormBlock">
              <label className="EntryLabel">メール送付先</label>
              <div className="inputAreaWrapper">
                <div className="inputArea">{email}</div>
              </div>
            </div>
          </div>
          <div className="EntryBodyItem">
            <LineTitle text="緊急連絡先" className="flexTitle">
              <div className="optional optionalPC">
                {optionalMessage}
              </div>
            </LineTitle>
            <div className="optional optionalSP">
              {optionalMessage}
            </div>
            <div className="EntryFormBlock">
              <label className="EntryLabel">お名前</label>
              <div className="inputAreaWrapper">
                <div className="inputArea">
                  <div className="inputName">
                    <div className="inputSizeName inputEmergencyName">
                      <input
                        id="input.member.emergencyContact.name"
                        type="text"
                        placeholder="お名前"
                        value={nameEmergency}
                        onChange={(e) => setNameEmergency(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="inputAreaSup">漢字・ひらがな・英数 32文字以内</div>
              </div>
            </div>
            <div className="EntryFormBlock">
              <label className="EntryLabel">続柄</label>
              <div className="inputAreaWrapper">
                <div className="inputArea">
                  <SelectBox
                    options={relationShipOptions}
                    value={relationshipEmergency}
                    onChange={(e) => setRelationshipEmergency(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div id="input.member.emergencyContact.tel.full" className="EntryFormBlock">
              <label className="EntryLabel">電話番号</label>
              <div className="inputAreaWrapper">
                <div className="inputArea">
                  <div className="inputName">
                    <div className="inputSizeNumber">
                      <input
                        id="input.member.emergencyContact.tel.first"
                        type="text"
                        placeholder="03"
                        value={phoneNumberEmergency1}
                        onChange={(e) => setPhoneNumberEmergency1(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="inputSymbol">－</div>
                  <div className="inputName">
                    <div className="inputSizeNumber">
                      <input
                        id="input.member.emergencyContact.tel.second"
                        type="text"
                        placeholder="1234"
                        value={phoneNumberEmergency2}
                        onChange={(e) => setPhoneNumberEmergency2(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="inputSymbol">－</div>
                  <div className="inputName">
                    <div className="inputSizeNumber">
                      <input
                        id="input.member.emergencyContact.tel.third"
                        type="text"
                        placeholder="5678"
                        value={phoneNumberEmergency3}
                        onChange={(e) => setPhoneNumberEmergency3(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="inputAreaSup">半角数字</div>
              </div>
            </div>
          </div>

          {customers.length > 1 && (
            <div className="EntryBodyItem">
              <LineTitle text="同行者情報" className="flexTitle"><span className="required">※必須</span></LineTitle>
              {customers.map((c, index) => {
                if (c.state.representative) {
                  return null;
                }

                const isInfant = c.state.type === CHARGE_KIND.infant;
                const isChildren2 = c.state.type === CHARGE_KIND.children2;
                return (
                  <React.Fragment key={index}> 
                    <div className="wrapperLabel">
                      {`同行者${index} ${CHARGE_KIND_DISPLAY[c.state.type]}`}
                      {(isInfant || (isChildren2 && !isAna2)) && (
                        <TooltipIcon>
                          <InfantCaption />
                        </TooltipIcon>
                      )}
                    </div>
                    <CustomerForm
                      index={index - 1}
                      id={`customer-${index}`}
                      customer={c}
                      femaleOnly={femaleOnly}
                      isAna2={isAna2}
                      indicatorOptions={indicatorOptions}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          )}

          <div className="EntryBodyItem EntryBodyItemRoom">
            <LineTitle text="部屋割り" className="flexTitle"><span className="required">※必須</span></LineTitle>
            {customers && customers.length && customers.map((c, index) => {
              if (index + 1 > peopleNumWithoutInfant) {
                // https://gitlab.fqsv.net/dp-plus/front/-/issues/359
                // 幼児は部屋割りは表示しない
                return null;
              }

              const {
                representative,
                lastName,
                firstName,
                lastPhoneticName,
                firstPhoneticName,
                room,
              } = c.state;
              const title = representative ? '代表者' : `同行者${index}`;
              const totalRoomNumber = representative ? 1 : numberOfRooms;
              const phoneticName = representative ? `${lastPhoneticName || ''} ${firstPhoneticName || ''}` : `${lastName || ''} ${firstName || ''}`;

              const name = !(lastName || firstName) ?
                '(未入力)' : `${phoneticName}`;

              return (
                <div className="EntryFormBlockRoom" key={index}>
                  <div className="EntryBodyItemRoomText">
                    <div className="EntryBodyItemRoomTitle">{title}</div>
                    <div className="EntryBodyItemRoomName">{name}</div>
                  </div>
                  <div className="EntryBodyItemRoomRadio">
                    <RadioRoom
                      id={`room-${index}`}
                      totalRoomNumber={totalRoomNumber}
                      value={room}
                      onChange={(v) => c.setters.setRoom(v)}
                      withOutline
                    />
                  </div>
                </div>
              );
            })}
            {haveAnyInfant && (
              <div className="EntryFormBlockRoom noteTextRed" >※こども(0～5歳)の寝具なし（添い寝）のお子様は部屋割りには含まれません。</div>
            )}
          </div>

          <div className="EntryBodyItem notesWrapper">
            <div className="noteItems">
              <div className="noteItem">
                <div className="noteItemBody">
                  <div className="caution">
                    <div className="cautionTitle">ご注意・ご案内</div>
                    <div className="cautionText">航空券についてお申込み時にご入力いただいたお名前・性別・ご年齢にて航空券の手配をいたします。ご搭乗される方の変更や、入力情報に誤りがある場合、ご搭乗いただけません。お客様負担で正規運賃にて新たに航空券をお買い求めいただき、ご搭乗いただくことがございますので、ご注意ください。</div>
                  </div>
                  <div className="caution">
                    <div className="noteTextRed cautionTitle">お子様（こども）について</div>
                    <div className="noteTextRed cautionText">ご年齢に関係なくご参加の皆様のご予約が必要となります。必ず「6～11歳」「3～8歳」「3～5歳」「2歳」「0～1歳」のいずれかでご予約をお願いいたします。下記「要望・連絡事項」へご入力いただいてもお承りできません。6歳以上のお子様は航空座席のみ（添い寝）のご予約はお取扱はできません</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="EntryBodyItem">
            <LineTitle text="要望・連絡事項" className="flexTitle"><div className="optional optionalPC">※任意</div></LineTitle>
            <div className="EntryFormBlockTextarea">
              <textarea
                cols="30"
                rows="10"
                className="textarea"
                placeholder="アメニティ・食事アレルギーなど施設へのご要望はこちらからご連絡お願いします。"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <div className="textareaSups">
                <div className="textareaSup">全角英数字300文字以内</div>
                <div className="noteTextRed textareaSup">出発3日前以内のご予約時は、施設へのご連絡・ご要望は直接宿泊施設へご連絡ください。</div>

                <div
                  className="textareaSup"
                >お宿へのご希望事項などは原則としてそのままお伝えいたします。 (例) 禁煙ルームのご要望（禁煙ルームをご希望の際、消毒対応になる場合がございます。）</div>
                <div
                  className="textareaSup"
                >※原則として、事前の回答はいたしかねます。現地にてご確認ください。 ※ご入力いただいた内容について、必ずしもお受けできない場合があります。 ※ご連絡時期や内容によって、ご回答ができない場合があります。</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showOtherSiteId && (
        <div className="EntryBlock">
          <LineTitleGray size="lg">{otherSiteIdData.otherSiteMemberIdHeader}</LineTitleGray>
          <div style={{ marginTop: 16 }}>
            <img className="isPC" src={otherSiteIdData.logoFileName} />
            <img className="isSP" src={otherSiteIdData.logoFileNameSp} />
          </div>
          <div className="EntryBody" style={{ paddingTop: 16 }}>
            <div className="lifeSupportTitle">
              <div>{otherSiteIdData.otherSiteMemberIdDispName}</div>
              {requiredOtherSiteId && <div className="required">※必須</div>}
            </div>
            <div className="lifeSupportInput">
              {editOtherSiteId ? (
                <input
                  id="input.otherSiteId.otherSiteMemberId"
                  type="text"
                  placeholder="0123456789"
                  value={otherSiteMemberId || ''}
                  onChange={(e) => setOtherSiteMemberId(e.target.value)}
                />
              ) : (
                <span>{otherSiteMemberId}</span>
              )}
            </div>
            <div className="lifeSupportSups">
              <div className="lifeSupportSup">{inputAttributeDisplay}</div>
              <div className="lifeSupportSup" dangerouslySetInnerHTML={{ __html: otherSiteIdData.fixedWording }} />
            </div>
          </div>
        </div>
      )}
      <div className="EntryActions">
        <div className="linkActions">
          <div className="linkMypage">
            <LinkButton buttonType="cta" onClick={handleConfirm}>最終確認へ進む</LinkButton>
          </div>
        </div>
      </div>
      <form ref={agreementFormRef} action={environments.DP_AGREEMENT_URI} method="POST" style={{ display: 'none'}}>
        <input hidden name="session" value={session.id} readOnly />
        <input hidden name="r_url" value={agreeCallbackUrl} readOnly />
        <input hidden name="back_url" value={agreeCallbackUrl} readOnly />
        {prefectureID && <input hidden name="pref_code" value={prefectureID} readOnly />}
        {hotelID && <input hidden name="hotel_code" value={hotelID} readOnly />}
        {userId && <input hidden name="userid" value={userId} readOnly />}
        <input hidden name="dep_date" value={departureDate} readOnly />
        {stayNights && <input hidden name="stay_num" value={stayNights} readOnly />}
        <input hidden name="arr_date" value={arrivalDate} readOnly />
      </form>
    </div>
  );
};

Entry.propTypes = {
  session: PropTypes.object,
};

export default Entry;

const useCustomer = (initialType, isRepresentative = false, member, femaleOnly, isAna2) => {
  const [representative, setRepresentative] = useState(isRepresentative);

  const initialFirstName = member ? member.name.first : '';
  const initialLastName = member ? member.name.last : '';
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);

  const initialFirstPhoneticName = isRepresentative && member ? member.phoneticName.first : '';
  const initialLastPhoneticName = isRepresentative && member ? member.phoneticName.last : '';
  const [firstPhoneticName, setFirstPhoneticName] = useState(initialFirstPhoneticName);
  const [lastPhoneticName, setLastPhoneticName] = useState(initialLastPhoneticName);

  const initialFirstAlphabetName = isRepresentative && member ? member.alphabetName.first : '';
  const initialLastAlphabetName = isRepresentative && member ? member.alphabetName.last : '';
  const [firstAlphabetName, setFirstAlphabetName] = useState(initialFirstAlphabetName);
  const [lastAlphabetName, setLastAlphabetName] = useState(initialLastAlphabetName);

  const initialBirthYear = member && member.birthday ? moment(member.birthday, 'YYYY-MMDD').format('YYYY') : '';
  const initialBirthMonth = member && member.birthday ? moment(member.birthday, 'YYYY-MMDD').format('M') : '';
  const initialBirthDate = member && member.birthday ? moment(member.birthday, 'YYYY-MMDD').format('D') : '';
  const [birthYear, setBirthYear] = useState(initialBirthYear);
  const [birthMonth, setBirthMonth] = useState(initialBirthMonth);
  const [birthDate, setBirthDate] = useState(initialBirthDate);
  const initialGender = femaleOnly ? 'FAMALE' : member ? member.sex : null;
  const [gender, setGender] = useState(initialGender);

  const initialPhoneType = isRepresentative && member ? member.contact.type : 'MOBILE';
  const initialPhoneNumber1 = isRepresentative && member ? member.contact.tel.first : '';
  const initialPhoneNumber2 = isRepresentative && member ? member.contact.tel.second : '';
  const initialPhoneNumber3 = isRepresentative && member ? member.contact.tel.third : '';
  const [phoneType, setPhoneType] = useState(initialPhoneType);
  const [phoneNumber1, setPhoneNumber1] = useState(initialPhoneNumber1);
  const [phoneNumber2, setPhoneNumber2] = useState(initialPhoneNumber2);
  const [phoneNumber3, setPhoneNumber3] = useState(initialPhoneNumber3);

  const initialPostalCode1 = isRepresentative && member && member.postalCode ?  member.postalCode.substring(0, 3) : '';
  const initialPostalCode2 = isRepresentative && member && member.postalCode ?  member.postalCode.substring(3, 7) : '';
  const initialPrefectureId = isRepresentative && member && member.prefectureID ? member.prefectureID : '13';
  const initialAddress1 = isRepresentative && member ? member.address1 || '' : '';
  const initialAddress2 = isRepresentative && member ? member.address2 || '' : '';
  const [postalCode1, setPostalCode1] = useState(initialPostalCode1);
  const [postalCode2, setPostalCode2] = useState(initialPostalCode2);
  const [prefectureId, setPrefectureId] = useState(initialPrefectureId);
  const [address1, setAddress1] = useState(initialAddress1);
  const [address2, setAddress2] = useState(initialAddress2);

  const [type, setType] = useState(initialType);
  const isInfant = initialType === CHARGE_KIND.infant;
  const isChildren2 = initialType === CHARGE_KIND.children2;
  const isChildrenC = initialType === CHARGE_KIND.childrenC;
  const initialIndicator = (isInfant || (isChildren2 && !isAna2)) ? '1' : undefined;
  const [infantIndicator, setInfantIndicator] = useState(initialIndicator);

  const initialRoom = isRepresentative ? 1 : member ? member.roomAllocation : 1;
  const [room, setRoom] = useState((isInfant || isChildren2 || isChildrenC)? undefined : initialRoom);

  const state = {
    representative,
    firstName,
    lastName,
    firstPhoneticName,
    lastPhoneticName,
    firstAlphabetName,
    lastAlphabetName,
    birthYear,
    birthMonth,
    birthDate,
    gender,
    phoneType,
    phoneNumber1,
    phoneNumber2,
    phoneNumber3,
    postalCode1,
    postalCode2,
    prefectureId,
    address1,
    address2,
    type,
    infantIndicator,
    room,
  };

  const setters = {
    setRepresentative,
    setFirstName,
    setLastName,
    setFirstPhoneticName,
    setLastPhoneticName,
    setFirstAlphabetName,
    setLastAlphabetName,
    setBirthYear,
    setBirthMonth,
    setBirthDate,
    setGender,
    setPhoneType,
    setPhoneNumber1,
    setPhoneNumber2,
    setPhoneNumber3,
    setPostalCode1,
    setPostalCode2,
    setPrefectureId,
    setAddress1,
    setAddress2,
    setType,
    setInfantIndicator,
    setRoom,
  };
  useEffect(() => {
    const converted_lastName = representative ? getRomanAlphabetString(lastPhoneticName) : getRomanAlphabetString(lastName);
    const converted_firstName = representative ? getRomanAlphabetString(firstPhoneticName) : getRomanAlphabetString(firstName);
    setLastAlphabetName(converted_lastName);
    setFirstAlphabetName(converted_firstName);
  }, [lastPhoneticName, firstPhoneticName, lastName, firstName, representative]);

  return { state, setters };
};

const initCustomers = (session) => {
  const { member, accompanies, people, plans, flightSchedule } = session;
  let { adult, childrenA, childrenB, childrenC, children2, infant } = people;
  if (!childrenA) {
    childrenA = 0;
  }
  if (!childrenB) {
    childrenB = 0;
  }
  if(!childrenC){
    childrenC = 0;
  }
  if(!children2){
    children2 = 0;
  }
  if (!infant) {
    infant = 0;
  }
  const totalCutomersNumber = adult + childrenA + childrenB + infant + childrenC + children2;

  const femaleOnly = plans && plans[0].femaleOnlyPlan;

  return createEmptyArray(totalCutomersNumber).map(( _, i) => {
    const representative = i === 0;
    let type = CHARGE_KIND.adult;
    if (i >= adult) {
      if (i >= adult + childrenA + childrenB + childrenC + children2) {
        type = CHARGE_KIND.infant;
      } else if (i >= adult + childrenA + childrenB + childrenC) {  
        type = CHARGE_KIND.children2;
      } else if (i >= adult + childrenA + childrenB) {
        type = CHARGE_KIND.childrenC;
      } else if(i >= adult + childrenA){
        type = CHARGE_KIND.childrenB;
      } else {
        type = CHARGE_KIND.childrenA;
      }
    }
    const initialState = representative ? member : (accompanies && accompanies.length) ? accompanies[i - 1] : undefined;

    //ANAPhase2判定
    const SelectedOutboundFlight = flightSchedule.departure;
    const SelectedOutboundDate = SelectedOutboundFlight.departureTime.substr(0, 10);
    const isAna2 = getIsAna2(SelectedOutboundFlight.airline, SelectedOutboundDate);
    
    return useCustomer(type, representative, initialState, femaleOnly, isAna2);
  });
};

const useCustomerInfo = (session) => {

  let customers = initCustomers(session);

  const { member } = session;

  const initialEmergencyContact = member && member.emergencyContact;
  const initialNameEmergency = initialEmergencyContact ? initialEmergencyContact.name || '' : '';
  const initialRelationshipEmergency = initialEmergencyContact && initialEmergencyContact.relationship ? initialEmergencyContact.relationship : undefined;
  const initialPhoneNumberEmergency1 = initialEmergencyContact ? initialEmergencyContact.tel && initialEmergencyContact.tel.first : '';
  const initialPhoneNumberEmergency2 = initialEmergencyContact ? initialEmergencyContact.tel && initialEmergencyContact.tel.second : '';
  const initialPhoneNumberEmergency3 = initialEmergencyContact ? initialEmergencyContact.tel && initialEmergencyContact.tel.third : '';

  const [nameEmergency, setNameEmergency] = useState(initialNameEmergency);
  const [relationshipEmergency, setRelationshipEmergency] = useState(initialRelationshipEmergency);
  const [phoneNumberEmergency1, setPhoneNumberEmergency1] = useState(initialPhoneNumberEmergency1);
  const [phoneNumberEmergency2, setPhoneNumberEmergency2] = useState(initialPhoneNumberEmergency2);
  const [phoneNumberEmergency3, setPhoneNumberEmergency3] = useState(initialPhoneNumberEmergency3);

  const initialMessage = member ? member.note || '' : '';
  const [message, setMessage] = useState(initialMessage);

  const state = {
    customers,
    nameEmergency,
    relationshipEmergency,
    phoneNumberEmergency1,
    phoneNumberEmergency2,
    phoneNumberEmergency3,
    message,
  };

  const setters = {
    setAdultCustomers: customers.map((c) => c.setters),
    setNameEmergency,
    setRelationshipEmergency,
    setPhoneNumberEmergency1,
    setPhoneNumberEmergency2,
    setPhoneNumberEmergency3,
    setMessage,
  };

  return { state, setters };
};

// const validateState = ({ customers, lifeSupportNumber }) => {
//   const validations = customers.map(validateCustomer);
//   //TODO lifeSupportNumberのハンドリングをいれる
//   return customers.length === validations.filter(Boolean).length;
//   // return lifeSupportNumber && customers.length === validations.filter(Boolean).length;
// };

// const validateCustomer = ({ state: customerState }) => {
//   const {
//     representative,
//     firstName,
//     lastName,
//     firstPhoneticName,
//     lastPhoneticName,
//     birthYear,
//     birthMonth,
//     birthDate,
//     gender,
//     phoneType,
//     phoneNumber1,
//     phoneNumber2,
//     phoneNumber3,
//     room,
//   } = customerState;

//   const essential = !!(firstName && lastName && birthYear && birthMonth && birthDate && gender && room);
//   const representativeValidation = !!(firstPhoneticName && lastPhoneticName &&
//                                       phoneType && phoneNumber1 && phoneNumber2 && phoneNumber3);

//   return representative ? essential && representativeValidation : essential;
// };
